// src/App.jsx - Enhanced with account deactivation handling
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { ApolloProvider } from '@apollo/client';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import client from './api/apollo-client';
import Login from './pages/auth/Login';
import AccountDeactivated from './pages/auth/AccountDeactivated'; // New component
import AdminDashboard from './pages/admin/Dashboard';
import LaboratoriesPage from './pages/admin/LaboratoriesPage';
import LaboratoryDetailPage from './pages/admin/LaboratoryDetailPage';
import LaboratoryDashboard from './pages/laboratory/Dashboard';
import AdminNotifications from './pages/admin/Notifications';
import Settings from './pages/admin/Settings';
import Users from './pages/admin/Users';
import Transactions from './pages/admin/Transactions';
import Message from './pages/admin/Messages';
import NotFound from './pages/NotFound';
import Sidebar from './components/common/Sidebar';
import PaymentRequests from './pages/admin/PaymentRequests';
import ContractAutomationPage from './pages/admin/ContractAutomationPage';
import FinancialSimulator from './pages/admin/FinancialSimulator';
import ReferralSettings from './pages/admin/Referral';
import ForgotPassword from './pages/auth/ForgotPassword';
import SetPassword from './pages/auth/SetPassword';
import Profile from './pages/profile/Profile';
import FraudCases from './pages/admin/FraudCases';
import Advertising from './pages/admin/Advertising';
import AdminPromotions from './pages/admin/AdminPromotions';
import { authApi } from './api/auth-api';

// Enhanced Protected Route component with account status checking
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const [isChecking, setIsChecking] = useState(true);
  const [shouldRedirectToDeactivated, setShouldRedirectToDeactivated] = useState(false);
  const isAuthenticated = authApi.isAuthenticated();
  const userType = authApi.getUserType();
  const location = useLocation();
  
  // Check account status when component mounts and periodically
  useEffect(() => {
    let statusCheckInterval;
    
    const checkAccountStatus = async () => {
      if (isAuthenticated) {
        try {
          const status = await authApi.checkAccountStatus();
          if (!status.isActive && status.shouldRedirect) {
            setShouldRedirectToDeactivated(true);
            setIsChecking(false);
            return;
          }
        } catch (error) {
          console.error('Error checking account status:', error);
        }
      }
      setIsChecking(false);
    };

    if (isAuthenticated) {
      checkAccountStatus();
      
      // Check account status every 5 minutes for active sessions
      statusCheckInterval = setInterval(checkAccountStatus, 5 * 60 * 1000);
    } else {
      setIsChecking(false);
    }

    return () => {
      if (statusCheckInterval) {
        clearInterval(statusCheckInterval);
      }
    };
  }, [isAuthenticated]);

  // Show loading while checking account status
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  // Redirect to account deactivated page if account is inactive
  if (shouldRedirectToDeactivated) {
    return <Navigate to="/account-deactivated" replace />;
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If roles are specified and user doesn't have permission, show not found
  if (allowedRoles.length > 0 && !allowedRoles.includes(userType)) {
    return <NotFound />;
  }
  
  return children;
};

// Layout component that includes the sidebar
const AppLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();

  const isAuthenticated = authApi.isAuthenticated();

  const showSidebar = isAuthenticated &&
    location.pathname !== '/login' &&
    location.pathname !== '/forgot-password' &&
    location.pathname !== '/account-deactivated';

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(true);
      } else if (window.innerWidth < 1024) {
        setSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on route change on mobile
  useEffect(() => {
    if (isMobile) setSidebarCollapsed(true);
  }, [location.pathname, isMobile]);

  const handleSidebarToggle = (collapsed) => {
    setSidebarCollapsed(collapsed);
  };

  if (!showSidebar) {
    return (
      <div className="min-h-screen bg-gray-100">
        {children}
      </div>
    );
  }

  const isFullHeightPage = location.pathname.startsWith('/admin/create-promotion');

  // Desktop: ml-16 (collapsed) or ml-64 (expanded) — Mobile: ml-0 always
  const contentMargin = isMobile
    ? 'ml-0'
    : sidebarCollapsed ? 'ml-16' : 'ml-64';

  return (
    <div className={`flex bg-gray-100 ${isFullHeightPage ? 'h-screen overflow-hidden' : 'min-h-screen'}`}>
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />
      <div
        className={`flex-1 min-w-0 transition-all duration-300 ${contentMargin} ${isFullHeightPage ? 'overflow-hidden flex flex-col' : 'overflow-x-hidden'}`}
      >
        {/* Mobile top bar */}
        {isMobile && (
          <div className="sticky top-0 z-10 flex items-center gap-3 bg-white border-b border-gray-200 px-4 py-3 shadow-sm">
            <button
              onClick={() => setSidebarCollapsed(prev => !prev)}
              className="p-1.5 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Menu"
            >
              {sidebarCollapsed ? <Menu size={22} /> : <X size={22} />}
            </button>
            <span className="text-sm font-semibold text-gray-700">MAZID Admin</span>
          </div>
        )}
        {isFullHeightPage ? (
          <div className="flex-1 min-h-0 overflow-hidden">
            {children}
          </div>
        ) : (
          <div className={`${isMobile ? 'p-3' : 'p-6'}`}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

// Component to handle default redirects based on user type
const DefaultRedirect = () => {
  const userType = authApi.getUserType();
  const isAuthenticated = authApi.isAuthenticated();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  switch (userType) {
    case 'ADMIN':
      return <Navigate to="/admin/dashboard" replace />;
    case 'LABORATORY':
      return <Navigate to="/laboratory/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  return (
    <ApolloProvider client={client}>
      <Router basename={process.env.REACT_APP_BASENAME || '/'}>
        <AppLayout>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/set-password/:token" element={<SetPassword />} />
            <Route path="/account-deactivated" element={<AccountDeactivated />} />
            
            {/* Admin Protected Routes */}
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/users" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Users />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/laboratories" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <LaboratoriesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/laboratories/:id" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <LaboratoryDetailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/messages" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Message />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/transactions" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Transactions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/payment-requests" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <PaymentRequests />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/create-promotion"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <ContractAutomationPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/settings/:tab" 
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Settings />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/admin/Notifications"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminNotifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/simulator"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <FinancialSimulator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/referral"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <ReferralSettings />
                </ProtectedRoute>
              }
            />

            {/* Laboratory Protected Routes */}
            <Route 
              path="/laboratory/*" 
              element={
                <ProtectedRoute allowedRoles={['LABORATORY']}>
                  <LaboratoryDashboard />
                </ProtectedRoute>
              } 
            />
            
            {/* Fraud cases — admin only */}
            <Route
              path="/admin/fraud-cases"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <FraudCases />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/advertising"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <Advertising />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin/promotions"
              element={
                <ProtectedRoute allowedRoles={['ADMIN']}>
                  <AdminPromotions />
                </ProtectedRoute>
              }
            />

            {/* Profile — accessible to both ADMIN and LABORATORY */}
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowedRoles={['ADMIN', 'LABORATORY']}>
                  <Profile />
                </ProtectedRoute>
              }
            />

            {/* Default Routes */}
            <Route path="/" element={<DefaultRedirect />} />

            {/* Catch-all route for 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
        
        {/* Toast Container for notifications */}
        <ToastContainer
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
          toastStyle={{
            fontSize: '14px',
            borderRadius: '8px'
          }}
        />
      </Router>
    </ApolloProvider>
  );
}

export default App;